import { normalizeAnalyticsRoute } from "@/shared/analytics/route-normalization";
import {
  isPublicWebAnalyticsAllowed,
  onPublicWebAnalyticsConsentChanged,
} from "@/shared/analytics/analytics-consent";

type VitalName = "LCP" | "CLS" | "INP" | "FCP" | "TTFB";

type VitalSlot = {
  value: number | null;
  sent: boolean;
  sessionValue?: number;
  sessionEntries?: PerformanceEntry[];
  worstDuration?: number;
};

const BEACON_URL = "/api/client-vitals";
const COLLECTED: Record<VitalName, VitalSlot> = {
  LCP: { value: null, sent: false },
  CLS: { value: null, sent: false, sessionValue: 0, sessionEntries: [] },
  INP: { value: null, sent: false, worstDuration: 0 },
  FCP: { value: null, sent: false },
  TTFB: { value: null, sent: false },
};

type PerformanceObserverInitWithDuration = PerformanceObserverInit & {
  durationThreshold?: number;
};

type NavigatorWithConnection = Navigator & {
  connection?: {
    effectiveType?: string;
  };
};

let installed = false;
let consentListenerInstalled = false;
let initialInstallAttempted = false;
let includeBufferedEntries = false;
const observers: PerformanceObserver[] = [];
let visibilityFlush: (() => void) | null = null;
let pageHideFlush: (() => void) | null = null;

export function installPublicWebVitalsReporter() {
  const isInitialInstallAttempt = !initialInstallAttempted;
  initialInstallAttempted = true;
  installConsentListener();
  if (!isPublicWebAnalyticsAllowed()) {
    uninstallPublicWebVitalsReporter();
    return;
  }
  if (installed) return;
  if (typeof window === "undefined") return;
  if (typeof PerformanceObserver === "undefined") return;
  installed = true;
  includeBufferedEntries = isInitialInstallAttempt;

  observeLcp();
  observeCls();
  observeInp();
  observeFcp();
  observeTtfb();

  visibilityFlush = () => {
    if (document.visibilityState !== "hidden") return;
    sendAll();
  };
  pageHideFlush = () => sendAll();
  document.addEventListener("visibilitychange", visibilityFlush);
  window.addEventListener("pagehide", pageHideFlush, { capture: true });
}

function installConsentListener() {
  if (consentListenerInstalled || typeof window === "undefined") return;
  consentListenerInstalled = true;
  onPublicWebAnalyticsConsentChanged(() => {
    if (isPublicWebAnalyticsAllowed()) {
      installPublicWebVitalsReporter();
    } else {
      uninstallPublicWebVitalsReporter();
    }
  });
}

function uninstallPublicWebVitalsReporter() {
  if (visibilityFlush) document.removeEventListener("visibilitychange", visibilityFlush);
  if (pageHideFlush) window.removeEventListener("pagehide", pageHideFlush, { capture: true });
  visibilityFlush = null;
  pageHideFlush = null;
  observers.splice(0).forEach((observer) => observer.disconnect());
  installed = false;
  includeBufferedEntries = false;
  resetCollectedVitals();
}

function resetCollectedVitals() {
  (Object.keys(COLLECTED) as VitalName[]).forEach((name) => {
    const slot = COLLECTED[name];
    slot.value = null;
    slot.sent = false;
    if (name === "CLS") {
      slot.sessionValue = 0;
      slot.sessionEntries = [];
    }
    if (name === "INP") slot.worstDuration = 0;
  });
}

function registerObserver(observer: PerformanceObserver) {
  observers.push(observer);
}

function observeLcp() {
  try {
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const last = entries[entries.length - 1];
      if (last) COLLECTED.LCP.value = Math.round(last.startTime);
    });
    observer.observe({ type: "largest-contentful-paint", buffered: includeBufferedEntries });
    registerObserver(observer);
  } catch {
    /* unsupported in this browser */
  }
}

function observeCls() {
  try {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        const shift = entry as PerformanceEntry & {
          hadRecentInput?: boolean;
          value?: number;
        };
        if (shift.hadRecentInput) continue;
        const value = Number(shift.value);
        if (!Number.isFinite(value)) continue;
        const slot = COLLECTED.CLS;
        const sessionEntries = slot.sessionEntries || [];
        const firstEntry = sessionEntries[0];
        const lastEntry = sessionEntries[sessionEntries.length - 1];
        if (
          firstEntry &&
          lastEntry &&
          (entry.startTime - lastEntry.startTime > 1000 ||
            entry.startTime - firstEntry.startTime > 5000)
        ) {
          slot.sessionValue = value;
          slot.sessionEntries = [entry];
        } else {
          slot.sessionValue = (slot.sessionValue || 0) + value;
          slot.sessionEntries = [...sessionEntries, entry];
        }
        if ((slot.sessionValue || 0) > (slot.value || 0)) {
          slot.value = Number((slot.sessionValue || 0).toFixed(4));
        }
      }
    });
    observer.observe({ type: "layout-shift", buffered: includeBufferedEntries });
    registerObserver(observer);
  } catch {
    /* unsupported in this browser */
  }
}

function observeInp() {
  try {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.duration > (COLLECTED.INP.worstDuration || 0)) {
          COLLECTED.INP.worstDuration = entry.duration;
          COLLECTED.INP.value = Math.round(entry.duration);
        }
      }
    });
    observer.observe({
      type: "event",
      buffered: includeBufferedEntries,
      durationThreshold: 16,
    } as PerformanceObserverInitWithDuration);
    registerObserver(observer);
  } catch {
    /* unsupported in this browser */
  }
}

function observeFcp() {
  try {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.name === "first-contentful-paint") {
          COLLECTED.FCP.value = Math.round(entry.startTime);
        }
      }
    });
    observer.observe({ type: "paint", buffered: includeBufferedEntries });
    registerObserver(observer);
  } catch {
    /* unsupported in this browser */
  }
}

function observeTtfb() {
  if (!includeBufferedEntries) return;
  try {
    const nav = performance.getEntriesByType("navigation")[0] as
      | PerformanceNavigationTiming
      | undefined;
    if (!nav) return;
    COLLECTED.TTFB.value = Math.max(0, Math.round(nav.responseStart - nav.requestStart));
  } catch {
    /* unsupported in this browser */
  }
}

function sendAll() {
  if (!isPublicWebAnalyticsAllowed()) return;
  (Object.keys(COLLECTED) as VitalName[]).forEach((name) => {
    const slot = COLLECTED[name];
    if (slot.sent || slot.value == null) return;
    sendOne(name, slot.value);
    slot.sent = true;
  });
}

function sendOne(name: VitalName, value: number) {
  if (!isPublicWebAnalyticsAllowed()) return;
  const body = JSON.stringify({
    name,
    value,
    lang: document.documentElement.lang || "en",
    path: normalizeAnalyticsRoute(window.location.pathname),
    effectiveType: (navigator as NavigatorWithConnection).connection?.effectiveType || null,
    capturedAtMs: Date.now(),
  });
  try {
    if (typeof navigator.sendBeacon === "function") {
      const queued = navigator.sendBeacon(
        BEACON_URL,
        new Blob([body], { type: "application/json" }),
      );
      if (queued) return;
    }
  } catch {
    /* fall through */
  }
  try {
    void fetch(BEACON_URL, {
      method: "POST",
      keepalive: true,
      headers: { "Content-Type": "application/json" },
      body,
    }).catch(() => null);
  } catch {
    /* swallow */
  }
}
